import { template } from "@ember/template-compiler";
import { hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import type Transition from '@ember/routing/transition';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import Ember from 'ember';
import type { EmberChangeset } from 'ember-changeset';
import { task } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import type ErrorsService from 'parog-web/services/errors';
import FormyActions from './actions';
import FormyCheckbox from './checkbox';
import FormyDate from './date';
import FormyField from './field';
import FormyInput from './input';
import FormySearch from './search';
import FormySelect from './select';
interface Signature {
    Element: HTMLFormElement;
    Args: {
        /** The EmberChangeset instance wrapping the model we want to edit */ changeset: EmberChangeset;
        /** The function to be executed after the changeset has been sucessfully saved */ onSuccess?: () => unknown;
    };
}
// TODO: add other hash fields and rename Hash
export interface Hash {
    changeset: EmberChangeset;
}
export default class FormyComponent extends Component<Signature> {
    @service
    errors: ErrorsService;
    @service
    router: RouterService;
    constructor(owner1: unknown, args1: Signature['Args']){
        super(owner1, args1);
        // Exiting the Formy is not only done by the form buttons
        // It could also happen when the user clicks on a link in the UI
        //
        // The Formy must then be informed when the router will transition
        // and in turn, alert the user if there are pending changes
        this.router.on('routeWillChange', this.showPendingChangesetAlert);
    }
    @task
    validateAndSave = function*(event1: SubmitEvent): unknown {
        event1.preventDefault();
        // @ts-expect-error cannot type this…
        yield this.args.changeset.validate();
        // @ts-expect-error cannot type this…
        if (this.args.changeset.isInvalid) {
            // @ts-expect-error cannot type this…
            this.errors.displayError('Veuillez corriger les erreurs avant de sauvegarder !');
            return;
        }
        try {
            // @ts-expect-error cannot type this…
            yield this.args.changeset.save();
            // @ts-expect-error cannot type this…
            yield this.args.onSuccess?.();
        } catch (error1) {
            if (!Ember.testing) console.error(error1);
            // @ts-expect-error cannot type this…
            this.errors.displayError("Nous sommes désolés, une erreur est survenue et nous n'avons pas pu sauvegarder vos modifications");
        }
    };
    @action
    private async showPendingChangesetAlert(transition1: Transition) {
        if (this.args.changeset.isPristine) return;
        if (this.stayingOnSameRoute(transition1)) return;
        transition1.abort();
        const response1 = await this.errors.showPendingChangesetError();
        if (response1.isDismissed) return;
        // The user really want to loose its modifications
        this.args.changeset.rollback();
        transition1.retry();
    }
    private stayingOnSameRoute(transition1: Transition): boolean {
        return !!transition1.to?.find((r1)=>r1.name === this.router.currentRouteName);
    }
    willDestroy(): void {
        this.router.off('routeWillChange', this.showPendingChangesetAlert);
        super.willDestroy();
    }
    static{
        template(`
    {{! prettier-ignore }}
    <form
      {{! @glint-expect-error }}
      {{on 'submit' (perform this.validateAndSave)}}
      ...attributes
      {{! @glint-expect-error }}
      class='ui form {{if @changeset.isInvalid "error"}} {{if this.validateAndSave.isRunning " loading"}}'
    >
      {{! @glint-expect-error }}
      {{yield
        (hash
          changeset=@changeset
          Actions=(component FormyActions formy=this)
          Field=(component FormyField formy=this)
          Input=(component FormyInput parent=this)
          Select=(component FormySelect parent=this)
          Date=(component FormyDate parent=this)
          Search=(component FormySearch parent=this)
          Checkbox=(component FormyCheckbox parent=this)
        )
      }}
    </form>
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
